export type IncidentLevelName =
  | "Good"
  | "Information"
  | "Warning"
  | "Error"
  | "Empty"
  | "Other";

type NormalizedIncidentLevel = Exclude<IncidentLevelName, "Other">;

const LEVEL_PRIORITY: Record<NormalizedIncidentLevel, number> = {
  Empty: 0,
  Good: 1,
  Information: 2,
  Warning: 3,
  Error: 4,
};

/**
 * Utility methods for incident level styling and comparisons.
 */
export default class IncidentLevel {
  public static readonly Good = "Good" as const;

  public static readonly Information = "Information" as const;

  public static readonly Warning = "Warning" as const;

  public static readonly Error = "Error" as const;

  public static readonly Empty = "Empty" as const;

  public static readonly Other = "Other" as const;

  public static readonly classes: Record<IncidentLevelName, string> = {
    Good: "board-ok",
    Information: "board-information",
    Warning: "board-warn",
    Error: "board-error",
    Empty: "board-empty",
    Other: "board-information",
  };

  public static classname(level: string): string {
    return IncidentLevel.classes[IncidentLevel.toLevelName(level)] ?? IncidentLevel.classes.Good;
  }

  public static max(levels: string[]): NormalizedIncidentLevel {
    if (levels.length === 0) {
      return IncidentLevel.Empty;
    }

    let maxLevel: NormalizedIncidentLevel = IncidentLevel.Empty;
    for (const level of levels) {
      const normalized = IncidentLevel.normalizeForComparison(level);
      if (LEVEL_PRIORITY[normalized] > LEVEL_PRIORITY[maxLevel]) {
        maxLevel = normalized;
      }
    }

    return maxLevel;
  }

  private static toLevelName(level: string): IncidentLevelName {
    if (level in IncidentLevel.classes) {
      return level as IncidentLevelName;
    }

    return IncidentLevel.Good;
  }

  private static normalizeForComparison(level: string): NormalizedIncidentLevel {
    const normalized = IncidentLevel.toLevelName(level);
    return normalized === IncidentLevel.Other ? IncidentLevel.Information : normalized;
  }
}
